import React, { useEffect, useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import CustomMap from '../components/CustomMap'; // Import the new CustomMap component
import { US_STATES } from '../utils/constants';
import { getCategoryAnalyst, getMapList, getSpentByEthnicityDetails } from '../api';
import InnerLoader from '../components/microComponents/InnerLoader';
import { useApp } from '../context/AppContext';
import GraphsWrapper from '../components/microComponents/wrapper/GraphsWrapper';

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

/*

       African American / Black
       White / Caucasian
       Asian (e.g., East Asian, South Asian, Southeast Asian)
       Hispanic / Latino / Latina / Latinx
       Native American / American Indian / Alaska Native
       Middle Eastern / North African (MENA)
       Native Hawaiian / Pacific Islander

       */

const InclusiveSourcing = () => {
    const { selectedOrg, setSelectedOrg, orgList, user, loading,
        setLoading } = useApp();
    const [selectedRegion, setSelectedRegion] = useState('all_states');

    const [ethnicityBarChart, setEthnicityBarChart] = useState([]);
    const [ethnicityBarChartXAxisData, setEthnicityBarChartXAxisData] = useState([]);

    const [spendByCategoryData, setSpendByCategoryData] = useState();

    const [mapList, setMapList] = useState([]);

    useEffect(() => {
        // if (selectedOrg) {
        getMapList(selectedOrg).then(data => {
            const shortStateNames = data.states;
            const US_STATES_LIST = shortStateNames && shortStateNames.length > 0 ? shortStateNames.map(state => US_STATES.find(s => s.label === state).label) : [];
            setMapList(US_STATES_LIST);
            setSelectedRegion(US_STATES_LIST[0] ?? '');
        })
        // } else {
        //     setMapList([]);
        // }
    }, [selectedOrg])

    useEffect(() => {
        setLoading(true);
        getSpentByEthnicityDetails(selectedRegion).then(async (response) => {
            const currentData = response?.answer;

            // Transform the currentData into the format required by BarChart
            const updatesEthnicityData = Object.keys(currentData).map((category, index) => {
                let finalObj = {}
                const allEthnicityInitials = Object.keys(Object.values(currentData)[index])
                allEthnicityInitials.map(ethnicity => (
                    finalObj[ethnicity] = currentData[category][ethnicity]
                ))

                return finalObj;
            });
            setEthnicityBarChart(updatesEthnicityData);
            setEthnicityBarChartXAxisData(Object.keys(currentData));
            getCategoryAnalyst(selectedRegion).then(data => {
                setSpendByCategoryData(data);
                setLoading(false);
            })

        }).catch((error) => {
            console.log('Error - ', error)
            setLoading(false);
        })
    }, [selectedRegion])

    const cropCategories = ['Fruits', 'Grains', 'Poultry & Eggs', 'Vegetables', 'Meat', 'Specialty Crops']


    const DoughnutChart = ({ title, data, category, className }) => {
        if (!data) return null;

        const spendData = data[category] ?? cropCategories.reduce((acc, category) => ({ ...acc, [category]: 0 }), {});

        const total = Object.values(spendData).reduce((acc, total) => acc + total, 0);

        function convertSpendData() {
            return Object.keys(spendData).map(key => ({
                name: key,
                value: spendData[key] ? spendData[key] > 100 ? 100 : spendData[key] : 0
            }));
        }

        const categoryData = convertSpendData();
        // Create modified data with minimum values when total is 0
        const chartData = total === 0
            ? categoryData.map(item => ({ ...item, value: 1 }))  // Set each segment to 1 for equal distribution
            : categoryData;

        const COLORS = ['#6366f1', '#fbbf24', '#34d399', '#f87171', '#60a5fa', '#3b82f6', '#3f3f3f', '#f9a8d4', '#fde68a'];

        return (
            <div className={`bg-white p-4 rounded-lg shadow-sm min-h-[600px] ${className}`}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">{title}</h2>
                </div>
                <div className="relative h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                            >
                                {categoryData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={total === 0 ? '#d3d3d3' : COLORS[index % COLORS.length]}
                                    />
                                ))}
                            </Pie>
                            <Legend
                                layout="vertical"
                                align="center"
                                verticalAlign="bottom"
                                formatter={(value, entry, index) => (
                                    <span style={{ color: '#374151' }}>
                                        {value}: {categoryData[index]?.value || 0}%
                                    </span>
                                )}
                            />
                            {total > 0 && <Tooltip formatter={(value) => `${value}%`} labelStyle={{ color: '#374151' }} />}
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    const spendByCategoryMockData = [
        {
            "Fruits": 0,
            "Grains": 0,
            "Poultry & Eggs": 0,
            "Vegetables": 0,
            'Meat': 0,
            'Specialty Crops': 0
        }
    ]

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <label className='flex items-center gap-2'>
                    <h1 className="text-2xl font-bold">Social Impact Dashboard</h1>
                    {loading && <InnerLoader
                        divSize={{ width: "30px", height: "30px" }}
                        loaderSize={{ width: "28px", height: "28px" }}
                    />}
                </label>
                <div className="flex justify-center items-center gap-4">

                    {/* Organization Dropdown for Super Admin */}
                    {user?.role === 'super_admin' && (
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Organization:</span>
                            <select
                                value={selectedOrg || ''}
                                onChange={(e) => setSelectedOrg(e.target.value)}
                                className="p-2 border rounded-md bg-white border-r-8 border-transparent"
                            >
                                <option value="">Select Organization</option>
                                {orgList.map(org => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Existing Region Filter */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Region Filter:</span>
                        <select
                            value={selectedRegion}
                            onChange={(e) => setSelectedRegion(e.target.value)}
                            className="p-2 border rounded-md bg-white border-r-8 border-transparent !min-w-[150px]"
                        >
                            {mapList && mapList.length > 0 && mapList.map((country, index) =>
                                <option key={index} value={country}>
                                    {country}
                                </option>
                            )}
                        </select>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Map Section */}
                <GraphsWrapper
                    // label="Geographic Distribution"
                    question={{
                        question_text: "State's data",
                    }}
                    className="!min-h-[400px] metric-card-animate"
                    childrenClassName="flex-col"
                >
                    <div className="flex flex-row justify-start items-start gap-2 my-4 w-full">
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-green-200 rounded"></div>
                            <span className="text-xs">Low Impact</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-green-600 rounded"></div>
                            <span className="text-xs">High Impact</span>
                        </div>
                    </div>
                    <CustomMap
                        geoUrl={geoUrl}
                        selectedRegion={selectedRegion}
                        setSelectedRegion={setSelectedRegion}
                        mapData={mapList}
                    />
                </GraphsWrapper>

                {/* Ethnicity Chart */}
                <GraphsWrapper
                    // label="Ethnic Distribution"
                    question={{
                        question_text: "Spend by Ethnicity per Region (%)"
                    }}
                    className="!min-h-[400px] metric-card-animate"
                >
                    <ResponsiveContainer width="100%" height={420}>
                        <BarChart
                            data={ethnicityBarChart}
                            margin={{ top: 0, right: 30, left: 20, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                tickFormatter={(value, index) => ethnicityBarChartXAxisData[index]
                                }
                                angle={-40}
                                textAnchor="end"
                                interval={0}
                                height={70}
                                tick={{
                                    // dy: 25,
                                    fontSize: 9
                                }}
                            />
                            <YAxis />
                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        return (
                                            <div className="bg-white p-2 border rounded shadow">
                                                <p className="text-sm font-semibold mb-2">{ethnicityBarChartXAxisData[label]}</p>
                                                <div className="flex flex-row gap-2">
                                                    {payload.map((entry, index) => (
                                                        <div key={index} className="flex items-center gap-2 text-sm">
                                                            <div
                                                                className="w-3 h-3"
                                                                style={{ backgroundColor: entry.color }}
                                                            />
                                                            <span>{entry.value}%</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                                formatter={(value) => `${value}%`}
                                position={{ y: 185 }}
                                cursor={{ fill: 'transparent' }}
                            />
                            <Legend layout='vertical' iconSize={10} />
                            <Bar dataKey="African American / Black" fill="#6366f1" />
                            <Bar dataKey="White / Caucasian" fill="#34d399" />
                            <Bar dataKey="Asian" fill="#fbbf24" />
                            <Bar dataKey="Hispanic / Latino / Latina / Latinx" fill="#f87171" />
                            <Bar dataKey="Native American / American Indian / Alaska Native" fill="#ea50fa" />
                            <Bar dataKey="Middle Eastern / North African (MENA)" fill="#60a5fa" />
                            <Bar dataKey="Native Hawaiian / Pacific Islander" fill="#3b82f6" />
                        </BarChart>
                    </ResponsiveContainer>
                </GraphsWrapper>

                {/* Donut Charts Grid */}
                <div className="col-span-1 lg:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {spendByCategoryData && spendByCategoryData.length > 0 ? spendByCategoryData.map((chartData, index) =>
                        <DoughnutChart title={`Spend by ${Object.keys(chartData)[0]} per Category`} totalSpend="500,000" data={chartData} category={Object.keys(chartData)[0]} className="metric-card-animate" />
                    ) : <DoughnutChart title={`Spend by ${Object.keys(spendByCategoryMockData)[0]} per Category`} totalSpend="500,000" data={spendByCategoryMockData} category={Object.keys(spendByCategoryMockData)[0]} className="metric-card-animate" />
                    }
                </div>
            </div>
        </div>
    );
};

export default InclusiveSourcing; 